export type Vec = [number, number, number];

export type Face = {
	/** центр грани; вершины кубика лежат на единичной сфере */
	center: Vec;
	normal: Vec;
	/** направление вправо и вниз в плоскости грани: так число на грани стоит ровно, когда грань смотрит на зрителя */
	right: Vec;
	down: Vec;
	/** вершины грани в её плоскости: [вправо, вниз] от центра */
	polygon: Array<[number, number]>;
	/** номер грани, 1…N; противоположные грани в сумме дают N + 1 */
	number: number;
};

const PHI = (1 + Math.sqrt(5)) / 2;

const dot = (a: Vec, b: Vec) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a: Vec, b: Vec): Vec => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const scale = (a: Vec, k: number): Vec => [a[0] * k, a[1] * k, a[2] * k];
const cross = (a: Vec, b: Vec): Vec => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const normalize = (a: Vec) => scale(a, 1 / Math.hypot(...a));

function signs(point: Vec): Vec[] {
	const result = new Map<string, Vec>();
	for (let mask = 0; mask < 8; mask++) {
		const v = point.map((c, i) => (mask & (1 << i) ? -c : c)) as Vec;
		result.set(v.map((c) => c.toFixed(6)).join(), v);
	}
	return [...result.values()];
}

const cyclic = (points: Vec[]): Vec[] => points.flatMap(([a, b, c]) => [[a, b, c], [b, c, a], [c, a, b]] as Vec[]);

const AXES = signs([1, 0, 0]).concat(signs([0, 1, 0]), signs([0, 0, 1]));
const CORNERS = signs([1, 1, 1]);
const ICOSAHEDRON = cyclic(signs([0, 1, PHI]));
const DODECAHEDRON = CORNERS.concat(cyclic(signs([0, PHI, 1 / PHI])));

function trapezohedron(): {vertices: Vec[]; normals: Vec[]} {
	const apex = 1.2;
	const cos36 = Math.cos(Math.PI / 5);
	const ring = (apex * (1 - cos36)) / (1 + cos36);
	const slope = (2 * apex) / (1 + cos36);
	const at = (deg: number, z: number): Vec => [Math.cos((deg * Math.PI) / 180), Math.sin((deg * Math.PI) / 180), z];
	const vertices: Vec[] = [[0, 0, apex], [0, 0, -apex]];
	const normals: Vec[] = [];
	for (let k = 0; k < 5; k++) {
		vertices.push(at(72 * k, ring), at(72 * k + 36, -ring));
		const top = at(72 * k + 36, 1);
		const bottom = at(72 * k + 72, -1);
		normals.push([top[0] * slope, top[1] * slope, 1], [bottom[0] * slope, bottom[1] * slope, -1]);
	}
	return {vertices, normals};
}

const SOLIDS: Record<number, () => {vertices: Vec[]; normals: Vec[]}> = {
	4: () => ({vertices: [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]], normals: [[-1, -1, -1], [-1, 1, 1], [1, -1, 1], [1, 1, -1]]}),
	6: () => ({vertices: CORNERS, normals: AXES}),
	8: () => ({vertices: AXES, normals: CORNERS}),
	10: trapezohedron,
	12: () => ({vertices: DODECAHEDRON, normals: ICOSAHEDRON}),
	20: () => ({vertices: ICOSAHEDRON, normals: DODECAHEDRON}),
};

function buildFaces(sides: number): Face[] {
	const {vertices, normals} = SOLIDS[sides]();
	const radius = Math.max(...vertices.map((v) => Math.hypot(...v)));
	const points = vertices.map((v) => scale(v, 1 / radius));
	const pole: Vec = [0, 0, 1];

	const faces = normals.map((raw) => {
		const normal = normalize(raw);
		const distances = points.map((p) => dot(p, normal));
		const max = Math.max(...distances);
		const corners = points.filter((_, i) => max - distances[i] < 1e-6);
		const center = scale(corners.reduce((sum, p) => [sum[0] + p[0], sum[1] + p[1], sum[2] + p[2]] as Vec, [0, 0, 0]), 1 / corners.length);
		// у квадрата число стоит ровно вдоль рёбер, у остальных граней смотрит в самую дальнюю от центра вершину:
		// так одинаковые грани развёрнуты одинаково, и число можно перенести на любую из них, не шевельнув кубик
		const reference: Vec = Math.abs(dot(normal, pole)) > 0.99 ? [0, 1, 0] : pole;
		const edge = normalize(sub(reference, scale(normal, dot(reference, normal))));
		const apex = corners.reduce((best, p) => (Math.hypot(...sub(p, center)) > Math.hypot(...sub(best, center)) + 1e-9 ? p : best));
		const up = sides === 6 ? edge : normalize(sub(apex, center));
		const down = scale(up, -1);
		const right = cross(down, normal);
		const polygon = corners
			.map((p) => [dot(sub(p, center), right), dot(sub(p, center), down)] as [number, number])
			.sort((a, b) => Math.atan2(a[1], a[0]) - Math.atan2(b[1], b[0]));
		return {center, normal, right, down, polygon, number: 0};
	});

	let low = 1;
	let high = faces.length;
	for (const face of faces) {
		if (face.number) continue;
		face.number = low++;
		const opposite = faces.find((other) => !other.number && dot(other.normal, face.normal) < -0.999);
		if (opposite) opposite.number = high--;
	}
	return faces;
}

const cache = new Map<number, Face[]>();

export function solidFaces(sides: number): Face[] {
	if (!cache.has(sides)) cache.set(sides, buildFaces(sides));
	return cache.get(sides)!;
}

const views = new Map<number, Array<{polygon: Array<[number, number]>; light: number}>>();

/**
 * Кубик, лежащий гранью вверх, как его видно со стороны стола: грани, повёрнутые вверх, в координатах
 * от −1 до 1. По этому виду рисуют кость в итогах броска — у каждого кубика он свой и узнаваемый.
 */
export function topView(sides: number): Array<{polygon: Array<[number, number]>; light: number}> {
	if (views.has(sides)) return views.get(sides)!;

	const faces = solidFaces(sides);
	const lying = faces[0];
	const seen = (v: Vec): Vec => [dot(v, lying.right), dot(v, lying.down), dot(v, lying.normal)];
	const corners = (face: Face) =>
		face.polygon.map(([x, y]) => seen([0, 1, 2].map((i) => face.center[i] + x * face.right[i] + y * face.down[i]) as Vec));

	const visible = faces.filter((face) => seen(face.normal)[2] > 1e-3);
	const radius = Math.max(...visible.flatMap((face) => corners(face).map((c) => Math.hypot(c[0], c[1]))));
	const view = visible
		.map((face) => ({polygon: corners(face).map(([x, y]) => [x / radius, y / radius] as [number, number]), light: seen(face.normal)[2]}))
		.sort((a, b) => a.light - b.light);

	views.set(sides, view);
	return view;
}

/** поворот кубика: x, y, z, w */
export type Quat = [number, number, number, number];

export const NO_TURN: Quat = [0, 0, 0, 1];

const quatNormalize = (q: Quat): Quat => {
	const length = Math.hypot(...q) || 1;
	return q.map((n) => n / length) as Quat;
};

export function quatTurn(q: Quat, v: Vec): Vec {
	const [x, y, z, w] = q;
	const u: Vec = [x, y, z];
	const first = cross(u, v);
	const second = cross(u, first);
	return [0, 1, 2].map((i) => v[i] + 2 * (w * first[i] + second[i])) as Vec;
}

/** поворот за время dt под угловой скоростью omega, радианы в секунду вокруг мировых осей */
export function quatSpin(q: Quat, omega: Vec, dt: number): Quat {
	const [x, y, z, w] = q;
	const [a, b, c] = omega;
	return quatNormalize([
		x + 0.5 * dt * (a * w + b * z - c * y),
		y + 0.5 * dt * (b * w + c * x - a * z),
		z + 0.5 * dt * (c * w + a * y - b * x),
		w - 0.5 * dt * (a * x + b * y + c * z),
	]);
}

/** поворот b, затем поворот a */
export function quatMultiply(a: Quat, b: Quat): Quat {
	const [ax, ay, az, aw] = a;
	const [bx, by, bz, bw] = b;
	return [
		aw * bx + ax * bw + ay * bz - az * by,
		aw * by - ax * bz + ay * bw + az * bx,
		aw * bz + ax * by - ay * bx + az * bw,
		aw * bw - ax * bx - ay * by - az * bz,
	];
}

export const quatAxisAngle = (axis: Vec, angle: number): Quat => {
	const half = Math.sin(angle / 2);
	return [axis[0] * half, axis[1] * half, axis[2] * half, Math.cos(angle / 2)];
};

/**
 * Чем заканчивается бросок: кубик заваливается на ту грань, на которую и так опирался. Это грань,
 * смотрящая вверх; её нужно опустить ровно, повернув кубик вокруг горизонтальной оси. Выпавшее
 * число потом читается с этой грани — куда кубик ляжет, никто заранее не решает.
 */
export function tipToFace(turn: Quat, faces: Face[]): {face: number; axis: Vec; angle: number} {
	const up: Vec = [0, 0, 1];
	const top = faces.reduce((best, face) => (quatTurn(turn, face.normal)[2] > quatTurn(turn, best.normal)[2] ? face : best));
	const normal = quatTurn(turn, top.normal);
	const axis = cross(normal, up);
	const length = Math.hypot(...axis);
	if (length < 1e-6) return {face: top.number, axis: up, angle: 0};
	return {face: top.number, axis: scale(axis, 1 / length), angle: Math.acos(Math.min(1, normal[2]))};
}

export const quatMatrix = (q: Quat): string => {
	const columns = [[1, 0, 0], [0, 1, 0], [0, 0, 1]].map((axis) => quatTurn(q, axis as Vec));
	return `matrix3d(${columns.flatMap((c) => [...c, 0]).concat(0, 0, 0, 1).map((n) => +n.toFixed(5)).join()})`;
};

export {dot, normalize};
